import {
  AMOUNT_INPUT_MODE,
  BiodigestateApplicationEvent1Payload,
  BiosolidsApplicationEvent1Payload,
  CompostApplicationEvent1Payload,
  FERTILIZER_TOTAL_AMOUNT_UNIT,
  FERTILIZER_TYPES,
  FertilizerEventPostPutBody,
  LimeApplicationEvent1Payload,
  NITROGEN_FERTILIZER_TYPE,
  ORGANIC_FERTILIZER_ANIMAL_TYPES,
  ORGANIC_FERTILIZER_APPLICATION_METHOD,
  ORGANIC_FERTILIZER_TYPES,
  OrganicFertilizerApplicationEvent1Payload,
  OtherApplicationEvent1Payload,
  SyntheticFertilizerApplicationEvent1Payload,
} from "model";

export type OrganicFertilizerApplicationEvent = {
  isImported: boolean;
  organicFertilizerType: ORGANIC_FERTILIZER_TYPES;
  animalType: ORGANIC_FERTILIZER_ANIMAL_TYPES[];
  applicationMethod: ORGANIC_FERTILIZER_APPLICATION_METHOD;
};

export type SyntheticFertilizerApplicationEvent = {
  productName: string;
  nPercentage: number;
  typeOfNitrogen: NITROGEN_FERTILIZER_TYPE;
  pPercentage?: number;
  kPercentage?: number;
};

export type LimeApplicationEvent = {
  phPriorToApplication: number;
};

export type AnyOtherFertilizerEvent = Record<string, never>;

export type FertilizerEventFormData = {
  note?: string;
  // Canonical absolute total applied (always stored). In RATE mode the user
  // edits `amountRate` and this is derived as rate × selected field area.
  totalAmount: number;
  totalAmountUnit: FERTILIZER_TOTAL_AMOUNT_UNIT;
  amountInputMode: AMOUNT_INPUT_MODE;
  // Transient: the per-hectare rate the user sees/edits in RATE mode. Not
  // persisted — re-derived from totalAmount and field area when editing.
  amountRate?: number;
  // Optional cost of the fertiliser product applied (GBP).
  productCost?: number;
  fertilizerApplicationDate: Date;
  fertilizerType: FERTILIZER_TYPES;
  fieldGuids: string[];
  fertilizerProperties:
    | OrganicFertilizerApplicationEvent
    | SyntheticFertilizerApplicationEvent
    | LimeApplicationEvent
    | AnyOtherFertilizerEvent;
};
export const convertFromFormInput = (
  fertilizerEventFormData: FertilizerEventFormData
): FertilizerEventPostPutBody => {
  // Cost is optional; the backend requires the currency unit only when a cost
  // is present, so omit both when there is no cost.
  const productCost =
    (fertilizerEventFormData.productCost as unknown) === "" ||
    fertilizerEventFormData.productCost == null
      ? undefined
      : Number(fertilizerEventFormData.productCost);

  let fertilizerProperties = {};
  if (fertilizerEventFormData.fertilizerType === FERTILIZER_TYPES.SYNTHETIC) {
    const syntheticFertilizerProperties =
      fertilizerEventFormData.fertilizerProperties as SyntheticFertilizerApplicationEvent;
    fertilizerProperties = {
      kPercentage: syntheticFertilizerProperties.kPercentage
        ? syntheticFertilizerProperties.kPercentage / 100
        : undefined,
      nPercentage: syntheticFertilizerProperties.nPercentage / 100,
      pPercentage: syntheticFertilizerProperties.pPercentage
        ? syntheticFertilizerProperties.pPercentage / 100
        : undefined,
      productName: syntheticFertilizerProperties.productName,
      typeOfNitrogen: syntheticFertilizerProperties.typeOfNitrogen,
    };
  } else if (
    fertilizerEventFormData.fertilizerType === FERTILIZER_TYPES.ORGANIC
  ) {
    const organicFertilizerProperties =
      fertilizerEventFormData.fertilizerProperties as OrganicFertilizerApplicationEvent;
    fertilizerProperties = {
      animalType: organicFertilizerProperties.animalType,
      applicationMethod: organicFertilizerProperties.applicationMethod,
      isImported: organicFertilizerProperties.isImported,
      organicFertilizerType: organicFertilizerProperties.organicFertilizerType,
    };
  } else if (fertilizerEventFormData.fertilizerType === FERTILIZER_TYPES.LIME) {
    const limeFertilizerProperties =
      fertilizerEventFormData.fertilizerProperties as LimeApplicationEvent;
    fertilizerProperties = {
      phPriorToApplication: limeFertilizerProperties.phPriorToApplication,
    };
  }
  return {
    note: fertilizerEventFormData.note,
    fertilizerProperties: {
      ...fertilizerProperties,
      fertilizerType: fertilizerEventFormData.fertilizerType,
    } as
      | OrganicFertilizerApplicationEvent1Payload
      | SyntheticFertilizerApplicationEvent1Payload
      | LimeApplicationEvent1Payload
      | BiosolidsApplicationEvent1Payload
      | BiodigestateApplicationEvent1Payload
      | CompostApplicationEvent1Payload
      | OtherApplicationEvent1Payload,
    fertilizerApplicationDate: new Date(
      fertilizerEventFormData.fertilizerApplicationDate
    ).toISOString(),
    fieldGuids: fertilizerEventFormData.fieldGuids,
    totalAmount: fertilizerEventFormData.totalAmount,
    totalAmountUnit: fertilizerEventFormData.totalAmountUnit,
    amountInputMode: fertilizerEventFormData.amountInputMode,
    // Only include cost (and its currency) when the user entered one — the
    // backend forbids the currency unit when there is no cost.
    ...(productCost !== undefined
      ? { productCost, productCostUnit: "GBP" }
      : {}),
  };
};
